import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import type { CSSProperties } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import CharterCabinFeatureList from 'components/Flights/CharterFlights/CharterCabinFeatureList'
import MaldivesCharterTableExpandedSocieteCell from './MaldivesCharterTableExpandedSocieteCell'
import {
  ExpandedSectionFareTd,
  ExpandedSectionStickyTd,
} from './MaldivesCharterTripOptionsTable'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const PendingCellPad = styled.div`
  padding: ${rem(12)};
`

/** Zero-height marker row between Baggage and Seats. The parent table attaches a ref callback to
 *  the inner span and measures its Y position so it can render the second pair of horizontal
 *  scroll arrows in {@link TableWrap} (outside the horizontal scroll track) — same overhang
 *  treatment as the column-header arrow pair. */
const ExpandedSectionAnchorTd = styled.td`
  padding: 0;
  border: none;
  line-height: 0;
  font-size: 0;
  height: 0;
`

const messages = defineMessages({
  baggageSection: {
    id: 'zcz2kGGq5yw_5xp_6UiB',
    defaultMessage: 'Baggage',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin baggage allowance list.',
  },
  seatsMealsSection: {
    id: 'sn72ng9h0P2OZz-elD-h',
    defaultMessage: 'Seats, meals and more',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin seats / meals / extras list.',
  },
  societeBenefitsSection: {
    id: 'yobG5UuhkJTkVWyNt84v',
    defaultMessage: '{loyaltyProgramName} benefits',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin loyalty (Société) earn breakdown.',
  },
  societeBenefitsPending: {
    id: '94CrSZAfSLXyP-29cK4C',
    defaultMessage: '-',
    description: 'Placeholder shown in the Maldives charter expanded row Société benefits cell when the cabin has no seats available — a simple dash since baggage/inclusions still render from the hardcoded cabin content.',
  },
})

interface Props {
  fareClassColumns: ReadonlyArray<FareClassWithReward>
  fareColumnCellStyle: CSSProperties
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  /** Pre-built points-earn calculation request for the hotel portion of the bundle, sourced from
   *  the row's `bundleData` (built in `useCalendarItemData`). Wired into the Société cell so it
   *  can render hotel earn alongside flight earn for cash cabins, and as the only earn line for
   *  reward cabins (where the flight is paid in points and earns no loyalty). */
  hotelLoyaltyCalculationRequest?: App.LoyaltyPointsEarnCalculationRequest
  /** Callback ref attached to a zero-height marker between the Baggage and Seats rows so the
   *  parent table can measure the marker's Y position and render the second pair of horizontal
   *  scroll arrows in `TableWrap` (outside the scroll track) at that same vertical level. */
  expandedAnchorRef?: React.RefCallback<HTMLSpanElement>
  /** Set of fare class columns that are unavailable (no seats / not logged in for reward). */
  unavailableColumns: Set<FareClassWithReward>
}

/**
 * Baggage + Seats/meals + Société benefits comparison rows that render directly under an
 * expanded date row in {@link MaldivesCharterTripOptionsTable}. Baggage and seats lists come
 * from the same hardcoded `CharterCabinFeatureList` used by the cabin selection modal, so
 * inclusions display identically across surfaces and remain visible even when a cabin has no
 * seats available. Only the Société benefits cell is data-driven and shows a "-" placeholder
 * when the cabin is unavailable.
 *
 * Returns sibling `<tr>` elements via a fragment so the rows slot directly into the table's
 * `<tbody>` and inherit the card-border/sticky-column treatment from the surrounding cells.
 */
function MaldivesCharterTripOptionsTableExpandedRow({
  fareClassColumns,
  fareColumnCellStyle,
  checkIn,
  checkOut,
  rooms,
  hotelLoyaltyCalculationRequest,
  expandedAnchorRef,
  unavailableColumns,
}: Props) {
  const intl = useIntl()
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const baggageSectionLabel = intl.formatMessage(messages.baggageSection)
  const seatsMealsSectionLabel = intl.formatMessage(messages.seatsMealsSection)
  const societeBenefitsSectionLabel = intl.formatMessage(
    messages.societeBenefitsSection,
    { loyaltyProgramName },
  )
  const societeBenefitsPendingLabel = intl.formatMessage(messages.societeBenefitsPending, { loyaltyProgramName })
  const lastFareColumnIndex = fareClassColumns.length - 1

  return <>
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow={false}>
        <BodyText variant="small" weight="bold">{baggageSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => (
        <ExpandedSectionFareTd
          key={fareClassColumn}
          style={fareColumnCellStyle}
          $isLastFareColumn={idx === lastFareColumnIndex}
          $isLastExpandedRow={false}
        >
          {/* Hardcoded baggage list shared with the cabin selection modal — renders regardless
              of seat availability so the inclusions stay visible even when a cabin is sold out. */}
          <PendingCellPad>
            <VerticalSpacer gap={8}>
              <CharterCabinFeatureList fareClass={toBaseFareClass(fareClassColumn)} section="baggage" />
            </VerticalSpacer>
          </PendingCellPad>
        </ExpandedSectionFareTd>
      ))}
    </tr>
    {expandedAnchorRef && (
      <tr aria-hidden>
        <ExpandedSectionAnchorTd colSpan={fareClassColumns.length + 1}>
          <span ref={expandedAnchorRef} />
        </ExpandedSectionAnchorTd>
      </tr>
    )}
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow={false}>
        <BodyText variant="small" weight="bold">{seatsMealsSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => (
        <ExpandedSectionFareTd
          key={fareClassColumn}
          style={fareColumnCellStyle}
          $isLastFareColumn={idx === lastFareColumnIndex}
          $isLastExpandedRow={false}
        >
          {/* Hardcoded inclusions list shared with the cabin selection modal — same icons / copy. */}
          <PendingCellPad>
            <VerticalSpacer gap={8}>
              <CharterCabinFeatureList fareClass={toBaseFareClass(fareClassColumn)} section="inclusions" />
            </VerticalSpacer>
          </PendingCellPad>
        </ExpandedSectionFareTd>
      ))}
    </tr>
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow>
        <BodyText variant="small" weight="bold">{societeBenefitsSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => {
        const unavailable = unavailableColumns.has(fareClassColumn)
        return (
          <ExpandedSectionFareTd
            key={fareClassColumn}
            style={fareColumnCellStyle}
            $isLastFareColumn={idx === lastFareColumnIndex}
            $isLastExpandedRow
          >
            {/* Earn is per-column: if the column itself has no seats, the earn line is
                meaningless (the user can't book it). Always show "-" instead. */}
            {unavailable && <PendingCellPad style={{ textAlign: 'center' }}><BodyText variant="small" colour="neutral-three">{societeBenefitsPendingLabel}</BodyText></PendingCellPad>}
            {!unavailable && <MaldivesCharterTableExpandedSocieteCell
              fareClass={toBaseFareClass(fareClassColumn)}
              checkIn={checkIn}
              checkOut={checkOut}
              rooms={rooms}
              isReward={isRewardFareClass(fareClassColumn)}
              hotelLoyaltyCalculationRequest={hotelLoyaltyCalculationRequest}
            />}
          </ExpandedSectionFareTd>
        )
      })}
    </tr>
  </>
}

export default MaldivesCharterTripOptionsTableExpandedRow
